/**
 * CDN 工具（PC 官网 / WAP 网页）
 *
 * 后台「CDN 加速」保存后，公开接口 /api/cdn/config 会下发各端域名（60 秒缓存）。
 * 这里在应用启动时拉取一次，之后通过 cdnUrl() 把源站资源地址替换为 CDN 地址。
 *
 * 说明：
 *  - 构建产物（JS/CSS/图片）的加速，需在构建时设置 PUBLIC_URL 指向 CDN/对象存储，
 *    对应后台「静态资源前缀」字段；
 *  - 运行时的 API 返回资源（相对路径或源站绝对地址）由 cdnUrl() 转换。
 */

const CACHE_KEY = 'cdn_config_cache';
const CACHE_TTL = 5 * 60 * 1000;
const DEFAULT_ORIGIN = window.location.origin;

let config = null;

function readLocalCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - parsed._ts > CACHE_TTL) return null;
    return parsed.data;
  } catch (e) {
    return null;
  }
}

function writeLocalCache(data) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ _ts: Date.now(), data }));
  } catch (e) { /* 隐私模式下忽略 */ }
}

/** 应用启动时调用一次；失败时静默降级（直连源站） */
export async function loadCdnConfig() {
  try {
    const res = await fetch('/api/cdn/config', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const json = await res.json();
    if (json && json.success) {
      config = json.data;
      writeLocalCache(config);
    }
  } catch (e) {
    if (!config) config = readLocalCache();
  }
  return config;
}

function getConfig() {
  if (config) return config;
  config = readLocalCache();
  return config;
}

/** 判断当前属于 PC 还是 WAP */
function detectScene() {
  if (typeof window === 'undefined') return 'pc';
  const ua = window.navigator.userAgent || '';
  const isMobile = /Android|iPhone|iPad|iPod|Windows Phone|Mobile/i.test(ua);
  return isMobile ? 'wap' : 'pc';
}

/** 返回指定端生效的域名前缀（未开启 CDN 时为源站） */
export function getCdnDomain(scene) {
  const cfg = getConfig();
  const key = scene || detectScene();
  if (cfg && cfg.enabled !== false && cfg[key] && cfg[key].enabled && cfg[key].domain) {
    return cfg[key].domain;
  }
  return (cfg && cfg.origin) || DEFAULT_ORIGIN;
}

/**
 * 把资源地址替换为 CDN 地址
 * @param {string} url 相对路径（/uploads/...）或绝对地址
 * @param {string} scene pc | wap | miniprogram，默认自动识别
 */
export function cdnUrl(url, scene) {
  if (!url || typeof url !== 'string') return url;
  if (url.indexOf('data:') === 0 || url.indexOf('blob:') === 0) return url;

  const cfg = getConfig();
  const key = scene || detectScene();
  const sceneCfg = (cfg && cfg[key]) || {};
  const cdnDomain = (cfg && cfg.enabled !== false && sceneCfg.enabled && sceneCfg.domain) ? sceneCfg.domain : '';
  if (!cdnDomain) return url;

  // 相对路径
  if (url.indexOf('http://') !== 0 && url.indexOf('https://') !== 0 && url.indexOf('//') !== 0) {
    const prefix = key === 'miniprogram' ? '' : (sceneCfg.assetPrefix || '');
    return prefix + (url.indexOf('/') === 0 ? url : '/' + url);
  }

  // 绝对地址：仅替换源站域名，外链保持原样
  const origin = (cfg && cfg.origin) || DEFAULT_ORIGIN;
  if (origin && url.indexOf(origin) === 0) {
    const prefix = key === 'miniprogram' ? '' : (sceneCfg.assetPrefix || '');
    return prefix + url.slice(origin.length);
  }
  return url;
}

export default { loadCdnConfig, cdnUrl, getCdnDomain };
